import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PodUploadService } from 'src/app/services/proof-of-delivery/pod-upload.service';

@Component({
  selector: 'app-public-upload',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-upload.component.html',
  styleUrls: ['./public-upload.component.scss']
})
export class PublicUploadComponent implements OnInit {
  token: string = '';
  files: File[] = [];
  isDragOver: boolean = false;
  isUploading: boolean = false;
  isVerifying: boolean = false;
  tokenValid: boolean = false;
  showConfirmation: boolean = true;
  confirmationType: 'collection' | 'delivery' | null = null;
  showReview: boolean = false;
  selectedPreviewIndex: number = 0;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error' = 'idle';
  statusMessage: string = '';
  uploadedFiles: Array<{ filename: string; url: string }> = [];
  validationErrors: { [key: string]: string[] } = {};
  bookingInfo: { booking_id?: number; email?: string; expires_at?: string } = {};

  private readonly allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
  private readonly maxFileSize = 10 * 1024 * 1024; // 10MB in bytes

  constructor(
    private route: ActivatedRoute,
    private podUploadService: PodUploadService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    if (this.token) {
      this.verifyToken();
    } else {
      this.setError('No upload token provided.');
    }
  }

  private verifyToken(): void {
    this.isVerifying = true;
    this.statusMessage = 'Verifying upload link...';

    this.podUploadService.verifyToken(this.token).subscribe({
      next: (response) => {
        this.isVerifying = false;
        if (response.success) {
          this.tokenValid = true;
          this.bookingInfo = response.data || {};
          this.statusMessage = '';
        } else {
          this.tokenValid = false;
          this.setError(response.message);
        }
      },
      error: (error) => {
        this.isVerifying = false;
        this.tokenValid = false;
        
        if (error.status === 403) {
          this.setError('Invalid or expired upload link.');
        } else if (error.status === 500) {
          this.setError(error.error?.message || 'Token verification failed. Please try again.');
        } else {
          this.setError('Unable to verify upload link. Please try again.');
        }
      }
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files) {
      this.handleFiles(Array.from(element.files));
    }
  }

  private handleFiles(newFiles: File[]): void {
    const validFiles = newFiles.filter(file => this.validateFile(file));
    this.files = [...this.files, ...validFiles];
    this.clearStatus();
  }

  private validateFile(file: File): boolean {
    if (!this.allowedTypes.includes(file.type)) {
      this.setError(`File "${file.name}" is not a supported format. Only JPG, PNG, and PDF files are allowed.`);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.setError(`File "${file.name}" is too large. Maximum file size is 10MB.`);
      return false;
    }

    return true;
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    
    // Adjust selected preview index if necessary
    if (this.showReview) {
      if (this.selectedPreviewIndex >= this.files.length) {
        this.selectedPreviewIndex = Math.max(0, this.files.length - 1);
      }
      
      // If no files left, go back to upload
      if (this.files.length === 0) {
        this.showReview = false;
      }
    }
    
    this.clearStatus();
  }

  uploadFiles(): void {
    if (this.files.length === 0) {
      this.setError('Please select at least one file to upload.');
      return;
    }

    if (!this.confirmationType) {
      this.setError('Document type not specified.');
      return;
    }

    this.isUploading = true;
    this.uploadStatus = 'uploading';
    this.statusMessage = 'Uploading files...';
    this.validationErrors = {};

    this.podUploadService.uploadFiles(this.token, this.files, this.confirmationType).subscribe({
      next: (response) => {
        this.isUploading = false;
        if (response.success) {
          this.uploadStatus = 'success';
          this.statusMessage = response.message;
          this.uploadedFiles = response.data?.files || [];
          this.files = [];
        } else {
          this.uploadStatus = 'error';
          this.statusMessage = response.message;
          this.validationErrors = response.errors || {};
        }
      },
      error: (error) => {
        this.isUploading = false;
        this.uploadStatus = 'error';
        
        if (error.status === 403) {
          this.statusMessage = 'Invalid or expired upload link.';
        } else if (error.status === 422) {
          this.statusMessage = 'File validation failed.';
          this.validationErrors = error.error?.errors || {};
        } else if (error.status === 500) {
          this.statusMessage = error.error?.message || 'Upload failed. Please try again.';
        } else {
          this.statusMessage = 'An unexpected error occurred. Please try again.';
        }
      }
    });
  }

  private setError(message: string): void {
    this.uploadStatus = 'error';
    this.statusMessage = message;
  }

  private clearStatus(): void {
    if (this.uploadStatus !== 'uploading') {
      this.uploadStatus = 'idle';
      this.statusMessage = '';
      this.validationErrors = {};
    }
  }

  getFileSizeString(size: number): string {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) return 'bi-file-earmark-image';
    if (file.type === 'application/pdf') return 'bi-file-earmark-pdf';
    return 'bi-file-earmark';
  }

  hasValidationErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0;
  }

  getValidationErrorsList(): string[] {
    const errors: string[] = [];
    Object.values(this.validationErrors).forEach(errorArray => {
      errors.push(...errorArray);
    });
    return errors;
  }

  confirmCollection(): void {
    this.confirmationType = 'collection';
    this.showConfirmation = false;
  }

  confirmDelivery(): void {
    this.confirmationType = 'delivery';
    this.showConfirmation = false;
  }

  goBackToConfirmation(): void {
    this.showConfirmation = true;
    this.confirmationType = null;
    this.showReview = false;
    this.files = [];
    this.clearStatus();
  }

  proceedToReview(): void {
    if (this.files.length === 0) {
      this.setError('Please select at least one file to review.');
      return;
    }
    this.showReview = true;
    this.selectedPreviewIndex = 0;
    this.clearStatus();
  }

  goBackToUpload(): void {
    this.showReview = false;
    this.clearStatus();
  }

  nextPreview(): void {
    if (this.selectedPreviewIndex < this.files.length - 1) {
      this.selectedPreviewIndex++;
    }
  }

  previousPreview(): void {
    if (this.selectedPreviewIndex > 0) {
      this.selectedPreviewIndex--;
    }
  }

  selectPreview(index: number): void {
    this.selectedPreviewIndex = index;
  }

  getFilePreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }
}